// API layer for Anadear Estates — connected to the FastAPI backend.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// attach JWT if present
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("anadear_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------- Properties ----------
export const fetchProperties = async (params = {}) => {
  const clean = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== "any") clean[k] = v;
  });
  const { data } = await client.get(`/properties`, { params: clean });
  return data;
};

export const fetchProperty = async (slug) => {
  const { data } = await client.get(`/properties/${slug}`);
  return data;
};

export const createProperty = async (payload) => (await client.post(`/properties`, payload)).data;
export const updateProperty = async (id, payload) => (await client.put(`/properties/${id}`, payload)).data;
export const deleteProperty = async (id) => (await client.delete(`/properties/${id}`)).data;

// ---------- Forms ----------
export const submitValuation = async (payload) => (await client.post(`/valuations`, payload)).data;
export const valuationLookup = async (postcode) => (await client.get(`/valuation/lookup`, { params: { postcode } })).data;
export const getAreaSoldPrices = async (postcode) => (await client.get(`/public/area-sold-prices`, { params: { postcode } })).data;
export const generateAreaCampaign = async (area, postcode) => (await client.post(`/marketing/area-campaign`, { area, postcode })).data;
export const submitEnquiry = async (payload) => (await client.post(`/enquiries`, payload)).data;
export const bookViewing = async (payload) => (await client.post(`/viewings`, payload)).data;

export const downloadValuationReport = async (payload) => {
  const res = await client.post(`/valuations/report`, payload, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `anadear-valuation-${payload.postcode || "report"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ---------- Auth ----------
export const registerUser = async (payload) => (await client.post(`/auth/register`, payload)).data;
export const loginUser = async (payload) => (await client.post(`/auth/login`, payload)).data;
export const getMe = async () => (await client.get(`/auth/me`)).data;

// ---------- Favourites ----------
export const getFavourites = async () => (await client.get(`/favourites`)).data;
export const addFavourite = async (id) => (await client.post(`/favourites/${id}`)).data;
export const removeFavourite = async (id) => (await client.delete(`/favourites/${id}`)).data;

// ---------- Concierge ----------
export const conciergeChat = async (message, sessionId) =>
  (await client.post(`/concierge/chat`, { message, session_id: sessionId })).data;

// ---------- Admin ----------
export const getLeads = async (params = {}) => (await client.get(`/leads`, { params })).data;
export const getLeadsCount = async () => (await client.get(`/leads/count`)).data;
export const updateLeadStage = async (id, stage) => (await client.put(`/leads/${id}/stage`, { stage })).data;
export const setLeadFollowup = async (id, date) => (await client.put(`/leads/${id}/followup`, { date })).data;
export const addLeadNote = async (id, text) => (await client.post(`/leads/${id}/notes`, { text })).data;
export const deleteLead = async (id) => (await client.delete(`/leads/${id}`)).data;
export const updateLeadDetails = async (id, details) => (await client.put(`/leads/${id}/details`, details)).data;
export const bulkSetLeadCategory = async (leadIds, category) => (await client.put(`/leads/bulk-category`, { leadIds, category })).data;
export const bulkDeleteLeads = async (leadIds) => (await client.put(`/leads/bulk-delete`, { leadIds })).data;

export const askHmo = async (payload) => (await client.post(`/hmo/ask`, payload)).data;
export const getInstagramFeed = async () => (await client.get(`/marketing/instagram/feed`)).data;

// ---------- Blog ----------
export const getBlogPosts = async () => (await client.get(`/blog`)).data;
export const getBlogPost = async (slug) => (await client.get(`/blog/${slug}`)).data;
export const adminGetBlog = async () => (await client.get(`/admin/blog`)).data;
export const adminCreateBlog = async (p) => (await client.post(`/admin/blog`, p)).data;
export const adminUpdateBlog = async (id, p) => (await client.put(`/admin/blog/${id}`, p)).data;
export const adminDeleteBlog = async (id) => (await client.delete(`/admin/blog/${id}`)).data;
export const publishBlogFromDraft = async (draftId, coverImage = "", category = "Market News") => (await client.post(`/admin/blog/from-draft`, { draftId, coverImage, category, siteUrl: window.location.origin })).data;
export const shareBlogPost = async (id, platform, url) => (await client.post(`/admin/blog/${id}/share`, { platform, url })).data;
export const getBlogAutoshare = async () => (await client.get(`/admin/blog-autoshare`)).data;
export const setBlogAutoshare = async (enabled) => (await client.put(`/admin/blog-autoshare`, { enabled })).data;

// ---------- Landlord Protection & background-check services ----------
export const getServiceCatalog = async () => (await client.get(`/portal/services/catalog`)).data;
export const requestService = async (serviceType, notes = "", propertyRef = "") => (await client.post(`/portal/services/request`, { serviceType, notes, propertyRef })).data;
export const getMyServiceRequests = async () => (await client.get(`/portal/services/requests`)).data;
export const adminGetServiceRequests = async (status = "") => (await client.get(`/admin/service-requests`, { params: status ? { status } : {} })).data;
export const adminUpdateServiceRequest = async (id, status, adminNote = "") => (await client.put(`/admin/service-requests/${id}`, { status, adminNote })).data;
export const adminDeleteServiceRequest = async (id) => (await client.delete(`/admin/service-requests/${id}`)).data;
export const getViewings = async () => (await client.get(`/viewings`)).data;
export const updateViewingStatus = async (id, status) => (await client.put(`/viewings/${id}/status`, { status })).data;
export const getAdminValuations = async () => (await client.get(`/valuations`)).data;
export const getAdminEnquiries = async () => (await client.get(`/enquiries`)).data;
export const deleteEnquiry = async (id) => (await client.delete(`/enquiries/${id}`)).data;
export const dedupeEnquiries = async () => (await client.post(`/enquiries/dedupe`)).data;
export const deleteValuation = async (id) => (await client.delete(`/valuations/${id}`)).data;
export const dedupeValuations = async () => (await client.post(`/valuations/dedupe`)).data;
export const updateValuationStatus = async (id, status) => (await client.put(`/valuations/${id}/status`, { status })).data;
export const updateEnquiryStatus = async (id, status) => (await client.put(`/enquiries/${id}/status`, { status })).data;
export const addLeadManual = async (payload) => (await client.post(`/leads`, payload)).data;
export const importLeads = async (leads) => (await client.post(`/leads/import`, { leads })).data;
export const convertLead = async (id, role) => (await client.post(`/leads/${id}/convert`, { role })).data;
export const setLeadCategory = async (id, category) => (await client.put(`/leads/${id}/category`, { category })).data;

// ---------- Admin: saved search manager ----------
export const getSavedSearches = async () => (await client.get(`/admin/saved-searches`)).data;
export const toggleSavedSearch = async (id, active) => (await client.put(`/admin/saved-searches/${id}`, { active })).data;
export const deleteSavedSearch = async (id) => (await client.delete(`/admin/saved-searches/${id}`)).data;

// ---------- Admin: suppression list (unsubscribes) ----------
export const getSuppressions = async (q = "") => (await client.get(`/admin/suppressions`, { params: { q } })).data;
export const addSuppression = async (email) => (await client.post(`/admin/suppressions`, { email })).data;
export const removeSuppression = async (email) => (await client.delete(`/admin/suppressions`, { params: { email } })).data;
export const exportSuppressionsCsv = async () => {
  const res = await client.get(`/admin/suppressions/export`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = `anadear-suppression-list.csv`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

// ---------- Anadear Intelligence (Phase 1) ----------
export const getCommandCentre = async () => (await client.get(`/intelligence/command-centre`)).data;
export const getLandlordOpportunities = async (params = {}) => (await client.get(`/intelligence/landlords`, { params })).data;
export const getSellerOpportunities = async (params = {}) => (await client.get(`/intelligence/sellers`, { params })).data;
export const getOwnerReport = async (id) => (await client.get(`/intelligence/owner/${id}`)).data;
export const explainOwner = async (id) => (await client.post(`/intelligence/owner/${id}/explain`)).data;
export const getIntelActions = async (status = "pending") => (await client.get(`/intelligence/actions`, { params: { status } })).data;
export const approveIntelAction = async (id, note = "") => (await client.post(`/intelligence/actions/${id}/approve`, { note })).data;
export const dismissIntelAction = async (id, note = "") => (await client.post(`/intelligence/actions/${id}/dismiss`, { note })).data;

// ---------- Intelligence Phase 2-6 ----------
export const getBuyerIntel = async () => (await client.get(`/intelligence/buyers`)).data;
export const getBuyerDetail = async (id) => (await client.get(`/intelligence/buyers/${id}`)).data;
export const matchForProperty = async (id) => (await client.get(`/intelligence/match/property/${id}`)).data;
export const matchForBuyer = async (id) => (await client.get(`/intelligence/match/buyer/${id}`)).data;
export const getIntentBoard = async () => (await client.get(`/intelligence/intent`)).data;
export const copilotAsk = async (message) => (await client.post(`/intelligence/copilot`, { message })).data;
export const draftEmail = async (payload) => (await client.post(`/intelligence/draft/email`, payload)).data;
export const draftLetter = async (payload) => (await client.post(`/intelligence/draft/letter`, payload)).data;
export const runValuation = async (payload) => (await client.post(`/valuation/estimate`, payload)).data;
export const saveEngineValuation = async (payload) => (await client.post(`/valuation/save`, payload)).data;
export const getRentEstimate = async (params) => (await client.get(`/intelligence/rent-estimate`, { params })).data;
export const getWithdrawn = async () => (await client.get(`/intelligence/withdrawn`)).data;
export const getAudiences = async () => (await client.get(`/intelligence/audiences`)).data;
export const genAdCopy = async (payload) => (await client.post(`/intelligence/ad-copy`, payload)).data;
export const getCampaignOptimisation = async () => (await client.get(`/intelligence/campaign-optimisation`)).data;
export const getAttribution = async () => (await client.get(`/intelligence/attribution`)).data;
export const getForecast = async () => (await client.get(`/intelligence/forecast`)).data;
export const getOffers = async () => (await client.get(`/intelligence/offers`)).data;
export const getFallThrough = async () => (await client.get(`/intelligence/fall-through`)).data;
export const triageMaintenance = async (payload) => (await client.post(`/intelligence/maintenance/triage`, payload)).data;
export const getInvestorOpportunities = async () => (await client.get(`/intelligence/investor-opportunities`)).data;
export const notifyMatchedBuyers = async (propertyId, threshold = 70) => (await client.post(`/intelligence/match/property/${propertyId}/notify`, { threshold })).data;
export const previewBlast = async (propertyId, threshold = 70) => (await client.get(`/intelligence/match/property/${propertyId}/preview`, { params: { threshold } })).data;
export const getBlastLog = async (propertyId) => (await client.get(`/intelligence/match/property/${propertyId}/blast-log`)).data;
export const enrichOwnerSignals = async (id) => (await client.post(`/intelligence/owner/${id}/enrich-signals`)).data;

// ---------- Admin: users ----------
export const getUsers = async () => (await client.get(`/admin/users`)).data;
export const createUser = async (payload) => (await client.post(`/admin/users`, payload)).data;
export const updateUser = async (id, payload) => (await client.put(`/admin/users/${id}`, payload)).data;
export const deleteUser = async (id) => (await client.delete(`/admin/users/${id}`)).data;

// ---------- Marketing / AI hub ----------
export const getKit = async () => (await client.get(`/marketing/settings`)).data;
export const updateKit = async (payload) => (await client.put(`/marketing/settings`, payload)).data;
export const generateContent = async (payload) => (await client.post(`/marketing/generate`, payload)).data;
export const getDrafts = async (params = {}) => (await client.get(`/marketing/drafts`, { params })).data;
export const setDraftStatus = async (id, status) => (await client.put(`/marketing/drafts/${id}/status`, { status })).data;
export const publishDraft = async (id, channel = "facebook", imageUrl = null) => (await client.post(`/marketing/drafts/${id}/publish`, { channel, imageUrl })).data;
export const searchCompaniesHouse = async (q) => (await client.get(`/marketing/companies-house/search`, { params: { q } })).data;
export const deleteDraft = async (id) => (await client.delete(`/marketing/drafts/${id}`)).data;
export const getCampaigns = async () => (await client.get(`/marketing/campaigns`)).data;
export const createCampaign = async (payload) => (await client.post(`/marketing/campaigns`, payload)).data;
export const getProspects = async () => (await client.get(`/marketing/prospects`)).data;
export const enrichProspect = async (id) => (await client.post(`/marketing/prospects/${id}/enrich`)).data;
export const convertProspect = async (id) => (await client.post(`/marketing/prospects/${id}/convert`)).data;
export const downloadProspectLetter = async (id) => {
  const res = await client.get(`/marketing/prospects/${id}/letter`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = `anadear-letter-${id}.pdf`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};
export const exportProspectsCsv = async () => {
  const res = await client.get(`/marketing/prospects/export`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = `anadear-prospects.csv`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

// ---------- Growth / Ads / Media / Scheduler ----------
export const createAd = async (p) => (await client.post(`/growth/ads`, p)).data;
export const getAds = async () => (await client.get(`/growth/ads`)).data;
export const setAdStatus = async (id, status) => (await client.put(`/growth/ads/${id}/status`, { status })).data;
export const getAdTemplates = async () => (await client.get(`/growth/ad-templates`)).data;
export const getMedia = async (q) => (await client.get(`/growth/media`, { params: q ? { q } : {} })).data;
export const addMedia = async (url, tag) => (await client.post(`/growth/media`, { url, tag })).data;
export const schedulePost = async (p) => (await client.post(`/growth/scheduled-posts`, p)).data;
export const getScheduledPosts = async () => (await client.get(`/growth/scheduled-posts`)).data;
export const getMarketRadar = async () => (await client.get(`/growth/market-radar`)).data;
export const getRentRadar = async () => (await client.get(`/growth/rent-radar`)).data;
export const getDealScores = async () => (await client.get(`/growth/deal-scores`)).data;
export const getLandRegistry = async (postcode) => (await client.get(`/growth/land-registry`, { params: { postcode } })).data;
export const getPropertyData = async (postcode) => (await client.get(`/growth/property-data`, { params: { postcode } })).data;
export const getTimeline = async (propertyId) => (await client.get(`/growth/timeline/${propertyId}`)).data;
export const getProspecting = async () => (await client.get(`/growth/prospecting`)).data;
export const getAreaIntel = async (postcode) => (await client.get(`/growth/area-intel`, { params: { postcode } })).data;
export const getDealIntel = async () => (await client.get(`/growth/deal-intel`)).data;
export const getSocialAnalytics = async () => (await client.get(`/marketing/social-analytics`)).data;
export const getLinkInBio = async () => (await client.get(`/linkinbio`)).data;
export const setLinkInBio = async (payload) => (await client.put(`/admin/linkinbio`, payload)).data;

// ---------- Phase-1 additions ----------
export const getAdminCalendar = async () => (await client.get(`/admin/calendar`)).data;
export const addCalendarEvent = async (p) => (await client.post(`/admin/calendar/events`, p)).data;
export const deleteCalendarEvent = async (id) => (await client.delete(`/admin/calendar/events/${id}`)).data;
export const createSavedSearch = async (p) => (await client.post(`/saved-searches`, p)).data;
export const getCrmPulse = async () => (await client.get(`/crm/pulse`)).data;
export const getAssistant = async () => (await client.get(`/crm/assistant`)).data;
export const assistantSend = async (payload) => (await client.post(`/crm/assistant/send`, payload)).data;
export const assistantDismiss = async (leadId) => (await client.post(`/crm/assistant/dismiss`, { leadId })).data;
export const getMyPhotos = async () => (await client.get(`/portal/photos`)).data;
export const getBio = async () => (await client.get(`/bio`)).data;
export const getBioAdmin = async () => (await client.get(`/marketing/bio`)).data;
export const saveBio = async (payload) => (await client.put(`/marketing/bio`, payload)).data;
export const getActivityFeed = async () => (await client.get(`/activity`)).data;
export const getIntegrationsStatus = async () => (await client.get(`/integrations/status`)).data;
export const toggleWatchlist = async (propertyId) => (await client.post(`/portal/watchlist/${propertyId}`)).data;
export const bulkEmailLeads = async (payload) => (await client.post(`/leads/bulk-email`, payload)).data;
export const emailLead = async (id, subject, message) => (await client.post(`/leads/${id}/email`, { subject, message })).data;
export const nudgeLead = async (id, payload) => (await client.post(`/leads/${id}/nudge`, payload)).data;
export const mergeLeads = async (primaryId, duplicateIds) => (await client.post(`/leads/merge`, { primaryId, duplicateIds })).data;
export const importLeadsFile = async (file) => {
  const fd = new FormData(); fd.append("file", file);
  return (await client.post(`/leads/import-file`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const generateProspects = async (payload) => (await client.post(`/marketing/prospects/generate`, payload)).data;
export const getMortgageRates = async () => (await client.get(`/market/mortgage-rates`)).data;
export const getReviews = async () => (await client.get(`/reviews`)).data;
export const submitReview = async (payload) => (await client.post(`/reviews`, payload)).data;
export const getAdminReviews = async () => (await client.get(`/admin/reviews`)).data;
export const setReviewStatus = async (id, status) => (await client.put(`/admin/reviews/${id}`, { status })).data;
export const getInvoices = async () => (await client.get(`/admin/invoices`)).data;
export const createInvoice = async (payload) => (await client.post(`/admin/invoices`, payload)).data;
export const setInvoiceStatus = async (id, status) => (await client.put(`/admin/invoices/${id}/status`, { status })).data;
export const getPhotos = async (params) => (await client.get(`/agency/photos`, { params })).data;
export const uploadPhoto = async (file, meta = {}) => {
  const up = await uploadChunked(file);
  const fd = new FormData(); fd.append("storedPath", up.path);
  Object.entries(meta).forEach(([k, v]) => { if (v != null) fd.append(k, v); });
  return (await client.post(`/agency/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const deletePhoto = async (id) => (await client.delete(`/agency/photos/${id}`)).data;
export const getPropertyLinks = async () => (await client.get(`/agency/property-links`)).data;
export const linkVendorProperty = async (payload) => (await client.post(`/agency/property-links`, payload)).data;
export const deletePropertyLink = async (id) => (await client.delete(`/agency/property-links/${id}`)).data;
export const getAdsInsights = async () => (await client.get(`/growth/ads/insights`)).data;
export const getAdsAudiences = async () => (await client.get(`/growth/ads/audiences`)).data;
export const optimiseAds = async () => (await client.post(`/growth/ads/optimise`)).data;

// ---------- CRM automation + AI ----------
export const getBriefing = async () => (await client.get(`/crm/briefing`)).data;
export const crmCopilot = async (message, session_id) => (await client.post(`/crm/copilot`, { message, session_id })).data;
export const summariseLead = async (id) => (await client.post(`/crm/leads/${id}/summarise`)).data;
export const draftReply = async (id) => (await client.post(`/crm/leads/${id}/draft-reply`)).data;
export const getDuplicates = async () => (await client.get(`/crm/leads/duplicates`)).data;
export const bulkLeadAction = async (leadIds, action, value) => (await client.post(`/crm/leads/bulk-action`, { leadIds, action, value })).data;
export const getStaleLeads = async () => (await client.get(`/crm/leads/stale`)).data;
export const downloadBrochure = async (slug, poster = false) => {
  const res = await client.get(`/growth/brochure/${slug}`, { params: { poster }, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = `anadear-${poster ? "poster" : "brochure"}-${slug}.pdf`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};
export const setPropertyApproval = async (id, approval) => (await client.put(`/growth/properties/${id}/approval`, { approval })).data;

// ---------- Uploads (Object Storage) ----------
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 512 * 1024;
export const uploadFile = async (file) => `${BACKEND}${(await uploadChunked(file)).url}`;
async function uploadChunked(file) {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  let data;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
    fd.append("upload_id", uploadId); fd.append("index", i); fd.append("total", total);
    fd.append("filename", file.name || "upload"); fd.append("content_type", file.type || "");
    ({ data } = await client.post(`/uploads/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  }
  return data;
}

// ---------- Onboarding: AML & Right-to-Rent ----------
export const getChecks = async (params = {}) => (await client.get(`/onboarding/checks`, { params })).data;
export const createCheck = async (payload) => (await client.post(`/onboarding/checks`, payload)).data;
export const runVerification = async (id) => (await client.post(`/onboarding/checks/${id}/verify`)).data;
export const resendCheckEmail = async (id) => (await client.post(`/onboarding/checks/${id}/resend`)).data;
export const getEmailLog = async () => (await client.get(`/onboarding/email-log`)).data;
export const getMyChecks = async () => (await client.get(`/onboarding/my-checks`)).data;
export const uploadMyComplianceDoc = async (file, docType) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("docType", docType);
  const { data } = await client.post(`/onboarding/my-checks/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

// ---------- Automation: Auto-Poster & Digests ----------
export const getAutomationSettings = async () => (await client.get(`/growth/automation/settings`)).data;
export const updateAutomationSettings = async (payload) => (await client.put(`/growth/automation/settings`, payload)).data;
export const runAutoPoster = async () => (await client.post(`/growth/automation/run-poster`)).data;
export const runDigest = async () => (await client.post(`/growth/automation/run-digest`)).data;
export const getAutomationQueue = async () => (await client.get(`/growth/automation/queue`)).data;
export const decideQueueItem = async (id, decision) => (await client.put(`/growth/automation/queue/${id}`, { decision })).data;

// ---------- Client Portals ----------
export const getPortalData = async () => (await client.get(`/portal/data`)).data;
export const payCharge = async (id) => (await client.post(`/portal/charges/${id}/pay`)).data;
export const reportIssue = async (payload) => (await client.post(`/portal/issues`, payload)).data;
export const sendPortalMessage = async (text) => (await client.post(`/portal/messages`, { text })).data;
export const toggleSavedAlerts = async (id, alerts) => (await client.put(`/portal/saved-searches/${id}/alerts`, { alerts })).data;
export const uploadComplianceCert = async (itemId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await client.post(`/portal/compliance/${itemId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const requestRenewal = async () => (await client.post(`/portal/renewal-request`)).data;
export const requestCallback = async () => (await client.post(`/portal/callback-request`)).data;
export const respondToOffer = async (index, decision) => (await client.post(`/portal/offers/${index}/respond`, { decision })).data;
export const downloadLandlordStatement = async () => {
  const res = await client.get(`/portal/owner-statement`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = `owner-statement.pdf`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

// ---------- Admin: maintenance & payments ----------
export const getAdminIssues = async () => (await client.get(`/admin/issues`)).data;
export const updateAdminIssue = async (id, payload) => (await client.put(`/admin/issues/${id}`, payload)).data;
export const createAdminIssue = async (payload) => (await client.post(`/admin/issues`, payload)).data;
export const deleteAdminIssue = async (id) => (await client.delete(`/admin/issues/${id}`)).data;
export const getAdminCharges = async (status) => (await client.get(`/admin/charges`, { params: status ? { status } : {} })).data;
export const confirmCharge = async (id) => (await client.post(`/admin/charges/${id}/confirm`)).data;
export const generateRent = async () => (await client.post(`/admin/charges/generate-rent`)).data;
export const emailOwnerStatements = async () => (await client.post(`/growth/automation/run-owner-statements`)).data;
export const downloadOwnerStatement = async (owner) => {
  const res = await client.get(`/admin/owner-statement`, { params: { owner }, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = `owner-statement-${(owner || "owner").replace(/\s+/g, "-").toLowerCase()}.pdf`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

// ---------- CRM: matching, deals, tasks, analytics ----------
export const getApplicants = async () => (await client.get(`/crm/applicants`)).data;
export const createApplicant = async (payload) => (await client.post(`/crm/applicants`, payload)).data;
export const getMatches = async () => (await client.get(`/crm/matches`)).data;
export const getDeals = async () => (await client.get(`/crm/deals`)).data;
export const createDeal = async (payload) => (await client.post(`/crm/deals`, payload)).data;
export const setDealStage = async (id, stage) => (await client.put(`/crm/deals/${id}/stage`, { stage })).data;
export const toggleDealTask = async (dealId, taskId) => (await client.put(`/crm/deals/${dealId}/tasks/${taskId}`)).data;
export const getTasks = async () => (await client.get(`/crm/tasks`)).data;
export const createTask = async (payload) => (await client.post(`/crm/tasks`, payload)).data;
export const toggleTask = async (id) => (await client.put(`/crm/tasks/${id}`)).data;
export const getAnalytics = async () => (await client.get(`/crm/analytics`)).data;
export const submitViewingFeedback = async (id, payload) => (await client.put(`/crm/viewings/${id}/feedback`, payload)).data;
export const downloadVendorReport = async (propertyId, slug) => {
  const res = await client.get(`/crm/vendor-report/${propertyId}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = `vendor-report-${slug || propertyId}.pdf`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

// ---------- Prospecting (Spectre) extras ----------
export const setProspectStage = async (id, stage) => (await client.put(`/marketing/prospects/${id}/stage`, { stage })).data;
export const getProspectRoi = async () => (await client.get(`/marketing/prospects/roi`)).data;
export const getProspectCampaigns = async () => (await client.get(`/marketing/prospect-campaigns`)).data;
export const createProspectCampaign = async (payload) => (await client.post(`/marketing/prospect-campaigns`, payload)).data;
export const enrolProspects = async (campaignId, prospectIds) => (await client.post(`/marketing/prospect-campaigns/${campaignId}/enrol`, { prospectIds })).data;
export const oneClickCampaign = async (propertyId) => (await client.post(`/marketing/one-click/${propertyId}`)).data;

// ---------- Agency: contractors, concierge, onboarding, agreements, developments, tracker ----------
export const getContractors = async () => (await client.get(`/agency/contractors`)).data;
export const createContractor = async (p) => (await client.post(`/agency/contractors`, p)).data;
export const getConciergePartners = async () => (await client.get(`/agency/concierge/partners`)).data;
export const createConciergeRequest = async (p) => (await client.post(`/agency/concierge/requests`, p)).data;
export const getMyConciergeRequests = async () => (await client.get(`/agency/concierge/my-requests`)).data;
export const getConciergeRequests = async () => (await client.get(`/agency/concierge/requests`)).data;
export const updateConciergeRequest = async (id, status) => (await client.put(`/agency/concierge/requests/${id}`, { status })).data;
export const getMyOnboarding = async () => (await client.get(`/agency/onboarding/me`)).data;
export const toggleOnboardingStep = async (stepId) => (await client.put(`/agency/onboarding/me/steps/${stepId}`)).data;
export const getAgreements = async () => (await client.get(`/agency/agreements`)).data;
export const createAgreement = async (p) => (await client.post(`/agency/agreements`, p)).data;
export const updateAgreement = async (id, p) => (await client.put(`/agency/agreements/${id}`, p)).data;
export const deleteAgreement = async (id) => (await client.delete(`/agency/agreements/${id}`)).data;
export const getDocTemplates = async () => (await client.get(`/agency/templates`)).data;
export const createDocTemplate = async (p) => (await client.post(`/agency/templates`, p)).data;
export const updateDocTemplate = async (id, p) => (await client.put(`/agency/templates/${id}`, p)).data;
export const deleteDocTemplate = async (id) => (await client.delete(`/agency/templates/${id}`)).data;
export const sendAgreement = async (id) => (await client.post(`/agency/agreements/${id}/send`)).data;
export const signAgreement = async (id, signer) => (await client.post(`/agency/agreements/${id}/sign`, { signer })).data;
export const downloadAgreementPdf = async (id) => {
  const res = await client.get(`/agency/agreements/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = `agreement-${id}.pdf`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};
export const getDevelopments = async () => (await client.get(`/agency/developments`)).data;
export const getDevelopment = async (id) => (await client.get(`/agency/developments/${id}`)).data;
export const createDevelopment = async (p) => (await client.post(`/agency/developments`, p)).data;
export const updateDevelopment = async (id, p) => (await client.put(`/agency/developments/${id}`, p)).data;
export const deleteDevelopment = async (id) => (await client.delete(`/agency/developments/${id}`)).data;
export const getFeaturedDevelopment = async () => (await client.get(`/agency/developments/featured`)).data;
export const setFeaturedDevelopment = async (id) => (await client.put(`/agency/developments/${id}/featured`)).data;
export const getFeaturedRotation = async () => (await client.get(`/agency/developments/rotation`)).data;
export const setFeaturedRotation = async (payload) => (await client.put(`/agency/developments/rotation`, payload)).data;
export const getHomeTracker = async () => (await client.get(`/agency/home-tracker`)).data;
export const toggleMilestone = async (id) => (await client.put(`/agency/home-tracker/milestones/${id}`)).data;

// ---------- Contractor jobs (from maintenance tickets) ----------
export const getJobs = async () => (await client.get(`/agency/jobs`)).data;
export const createJob = async (p) => (await client.post(`/agency/jobs`, p)).data;
export const updateJob = async (id, p) => (await client.put(`/agency/jobs/${id}`, p)).data;

// ---------- Concierge referral fees ----------
export const getReferrals = async () => (await client.get(`/agency/concierge/referrals`)).data;
export const setCommissionStatus = async (id, status) => (await client.put(`/agency/concierge/requests/${id}/commission`, { status })).data;

// ---------- Real e-sign (Dropbox Sign) signed PDF ----------
export const downloadSignedAgreement = async (id) => {
  const res = await client.get(`/agency/agreements/${id}/signed-pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = url; a.download = `signed-${id}.pdf`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

// ---------- Integrations / shared backend API keys ----------
export const getIntegrationInfo = async () => (await client.get(`/integrations/info`)).data;
export const getApiKeys = async () => (await client.get(`/integrations/keys`)).data;
export const createApiKey = async (p) => (await client.post(`/integrations/keys`, p)).data;
export const revokeApiKey = async (id) => (await client.post(`/integrations/keys/${id}/revoke`)).data;
export const getEmailStatus = async () => (await client.get(`/integrations/email/status`)).data;
export const sendTestEmail = async (to) => (await client.post(`/integrations/email/test`, { to })).data;
export const getEmailTemplate = async () => (await client.get(`/integrations/email/template`)).data;
export const updateEmailTemplate = async (p) => (await client.put(`/integrations/email/template`, p)).data;

// ---------- Inline AI writing assistant (emails / social / ads) ----------
export const aiAssist = async (payload) => (await client.post(`/crm/ai-assist`, payload)).data;

// ---------- AI Content Studio (social + ad generator with image creation) ----------
export const studioGenerate = async (payload) => (await client.post(`/marketing/studio`, payload, { timeout: 120000 })).data;

// ---------- Anadear Social AI: multi-platform, calendar, launches ----------
export const multiPlatformGenerate = async (payload) => (await client.post(`/marketing/multi-platform`, payload, { timeout: 120000 })).data;
export const getContentCalendar = async () => (await client.get(`/marketing/content-calendar`)).data;
export const buildContentCalendar = async (payload = {}) => (await client.post(`/marketing/content-calendar`, payload, { timeout: 120000 })).data;
export const launchCampaign = async (id, payload = {}) => (await client.post(`/marketing/launch-campaign/${id}`, payload, { timeout: 120000 })).data;
export const getAutoLaunch = async () => (await client.get(`/marketing/auto-launch`)).data;
export const setAutoLaunch = async (p) => (await client.put(`/marketing/auto-launch`, p)).data;
export const getPublishedPosts = async () => (await client.get(`/marketing/published-posts`)).data;
export const trackPageview = async (payload) => { try { return (await client.post(`/track/pageview`, payload)).data; } catch { return null; } };
export const scheduleDraft = async (id) => (await client.post(`/marketing/drafts/${id}/schedule`)).data;
export const scheduleWholeWeek = async () => (await client.post(`/marketing/content-calendar/schedule`)).data;


export const getBridgeStatus = async () => (await client.get(`/bridge/status`)).data;
export const syncAppListings = async () => (await client.post(`/bridge/sync-listings`)).data;

// ---------- Smart listings (auto-fill + AI copy) ----------
export const listingLookup = async (params) => (await client.get(`/listing-lookup`, { params })).data;
export const generateListing = async (payload) => (await client.post(`/properties/generate-listing`, payload)).data;

// ---------- Data import + clear demo ----------
export const downloadImportTemplate = async (kind = "properties") => {
  const res = await client.get(`/admin/import/template`, { params: { kind }, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = `anadear-${kind}-template.csv`;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};
export const importPropertiesFile = async (file) => {
  const fd = new FormData(); fd.append("file", file);
  return (await client.post(`/admin/import/properties-file`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const clearDemoData = async (payload) => (await client.post(`/admin/clear-demo-data`, payload)).data;

// ---------- Finance ----------
export const getFinanceSummary = async () => (await client.get(`/finance/summary`)).data;
export const getFinanceCategories = async () => (await client.get(`/finance/categories`)).data;
export const getTransactions = async (type) => (await client.get(`/finance/transactions`, { params: type ? { type } : {} })).data;
export const createTransaction = async (p) => (await client.post(`/finance/transactions`, p)).data;
export const updateTransaction = async (id, p) => (await client.put(`/finance/transactions/${id}`, p)).data;
export const deleteTransaction = async (id) => (await client.delete(`/finance/transactions/${id}`)).data;

// ---------- Meta lead-gen campaign builder ----------
export const createLeadCampaign = async (p) => (await client.post(`/growth/ads/lead-campaign`, p)).data;
export const getLeadCampaigns = async () => (await client.get(`/growth/ads/campaigns`)).data;

// ---------- LinkedIn ----------
export const getLinkedInStatus = async () => (await client.get(`/linkedin/status`)).data;
export const connectLinkedIn = async () => (await client.get(`/linkedin/connect`)).data;
export const disconnectLinkedIn = async () => (await client.post(`/linkedin/disconnect`)).data;
export const postLinkedIn = async (p) => (await client.post(`/linkedin/post`, p)).data;

// ---------- Auto-post rule + CRM tidy ----------
export const getAutoPost = async () => (await client.get(`/marketing/auto-post`)).data;
export const setAutoPost = async (p) => (await client.put(`/marketing/auto-post`, p)).data;
export const getAutoStudio = async () => (await client.get(`/marketing/auto-studio`)).data;
export const setAutoStudio = async (p) => (await client.put(`/marketing/auto-studio`, p)).data;
export const autoMergeDuplicates = async () => (await client.post(`/crm/leads/auto-merge`)).data;
export const getAutopilot = async () => (await client.get(`/crm/autopilot`)).data;
export const acceptAutopilot = async (kind) => (await client.post(`/crm/autopilot/accept`, { kind })).data;

// ---------- Weekly recap carousel ----------
export const getRecapSettings = async () => (await client.get(`/marketing/weekly-recap/settings`)).data;
export const setRecapSettings = async (p) => (await client.put(`/marketing/weekly-recap/settings`, p)).data;
export const generateWeeklyRecap = async () => (await client.post(`/marketing/weekly-recap`)).data;

// ---------- App Sync (partner) ----------
export const getAppSyncStatus = async () => (await client.get(`/partner/admin/sync-status`)).data;
export const runAppSync = async () => (await client.post(`/partner/admin/sync-now`)).data;
export const getSyncAudit = async () => (await client.get(`/partner/admin/sync-audit`)).data;
export const getAppConfig = async () => (await client.get(`/partner/admin/app-config`)).data;
export const saveAppConfig = async (payload) => (await client.put(`/partner/admin/app-config`, payload)).data;
export const testAppConfig = async (payload) => (await client.post(`/partner/admin/app-config/test`, payload || {})).data;
export const getAppConfigHistory = async () => (await client.get(`/partner/admin/app-config/history`)).data;



